import { shouldUseLocalizedShortcuts, type Hotkeys } from "owa-hotkeys";
import { getCurrentCulture } from "owa-localize";
import { defaultComposeActionHotkeys, defaultFormattingHotkeys, type LocalizedComposeCommand, } from "./composeHotkeyDefinitions";

type ComposeShortcutLocale = "da" | "de" | "es" | "fr" | "it" | "nb" | "nn" | "pt" | "pt-pt" | "sv";

type LocalizedFormattingShortcutOverrides = {
    bold: Hotkeys;
    italic: Hotkeys;
    underline: Hotkeys;
    shortcutsHandledByOtherCommands: Hotkeys;
};

const localizedComposeHotkeys: Record<ComposeShortcutLocale, Partial<Record<LocalizedComposeCommand, Hotkeys>>> = {
    da: {
        bold: "ctrl+f",
        italic: "ctrl+k",
        insertHyperlink: "ctrl+i",
    },
    de: {
        bold: "ctrl+shift+f",
        italic: "ctrl+shift+k",
        underline: "ctrl+u",
    },
    es: {
        bold: "ctrl+n",
        italic: "ctrl+k",
        underline: "ctrl+s",
        saveDraft: "ctrl+g",
        insertHyperlink: "ctrl+alt+k",
    },
    fr: {
        bold: "ctrl+g",
    },
    it: {
        bold: "ctrl+g",
        italic: "ctrl+i",
        underline: "ctrl+s",
        saveDraft: "ctrl+b",
    },
    nb: {
        bold: "ctrl+f",
        italic: "ctrl+k",
        insertHyperlink: "ctrl+i",
    },
    nn: {
        bold: "ctrl+f",
        italic: "ctrl+k",
        insertHyperlink: "ctrl+i",
    },
    pt: {
        bold: "ctrl+n",
        underline: "ctrl+s",
        saveDraft: "ctrl+b",
    },
    "pt-pt": {
        bold: "ctrl+n",
        underline: "ctrl+s",
        saveDraft: "ctrl+g",
    },
    sv: {
        bold: "ctrl+f",
        italic: "ctrl+k",
        insertHyperlink: "ctrl+i",
    },
};

/** Resolves the localized compose hotkey, falling back to the supplied default. */
export function resolveComposeHotkey(command: LocalizedComposeCommand, defaultHotkey: Hotkeys, currentCulture?: string): Hotkeys {
    if (!shouldUseLocalizedShortcuts()) {
        return defaultHotkey;
    }
    
    return resolveLocalizedComposeHotkey(command, defaultHotkey, currentCulture ?? getCurrentCulture());
}

export function getLocalizedFormattingShortcutOverrides(includeInsertHyperlinkShortcut: boolean = true): LocalizedFormattingShortcutOverrides | undefined {
    if (!shouldUseLocalizedShortcuts()) {
        return undefined;
    }
    
    const currentCulture = getCurrentCulture();
    return {
        bold: resolveLocalizedComposeHotkey("bold", defaultFormattingHotkeys.bold, currentCulture),
        italic: resolveLocalizedComposeHotkey("italic", defaultFormattingHotkeys.italic, currentCulture),
        underline: resolveLocalizedComposeHotkey("underline", defaultFormattingHotkeys.underline, currentCulture),
        shortcutsHandledByOtherCommands: includeInsertHyperlinkShortcut
            ? resolveLocalizedComposeHotkey("insertHyperlink", defaultComposeActionHotkeys.insertHyperlink, currentCulture)
            : [],
    };
}

function resolveLocalizedComposeHotkey(command: LocalizedComposeCommand, defaultHotkey: Hotkeys, currentCulture: string): Hotkeys {
    const culture = currentCulture.toLowerCase();
    const language = culture.split("-")[0];
    const shortcutLocale = (localizedComposeHotkeys[culture as ComposeShortcutLocale] ? culture : language) as ComposeShortcutLocale;
    
    // German Monarch also supports Ctrl+Shift+U for Underline.
    if (shortcutLocale == "de" && command == "underline") {
        return ["ctrl+u", "ctrl+shift+u"];
    }
    
    return localizedComposeHotkeys[shortcutLocale]?.[command] ?? defaultHotkey;
}
